import { Divider, Group, Select, Stack, Switch, Text } from '@mantine/core';
import { WidgetPropsComponentProps } from '../getWidgetPropsComponent';
import { WidgetType } from '../types';
import { useTranslation } from 'react-i18next';
import { KpiPropsComponent } from '../WebWidget/KpiProps';

// Default/fallback component when no specific component is available
const DefaultWidgetPropsComponent = () => {
  const { t } = useTranslation( 'widgets' );
  return (
    <div>
      <Text>
        {t( 'widget_builder_props_default_message','No specific configuration available for this widget type.' )}
      </Text>
    </div>
  );
};
// Shared component for cartesian chart props (area, line, bar)
const CartesianChartPropsComponent = ({ config, updateConfig }: WidgetPropsComponentProps ) => {
   const { t } = useTranslation( 'widgets' );
  const widgetComponentProps = config.widgetComponentProps || {};
  const dimensions = config.dimensions || [];
  const metrics = config.metrics || [];

  const handleUpdateProps = ( key: string, value: any ) => {
    const updatedProps = {
      ...widgetComponentProps,
      [key]: value
    };
    updateConfig( 'widgetComponentProps', updatedProps );
  };

  const getDimensionOptions = () => {
    return dimensions.map( dim => ({
      value: dim.field,
      label: dim.label || dim.field
    }));
  };

  const getMetricOptions = () => {
    return metrics.map( metric => ({
      value: metric.field,
      label: metric.label || `${metric.operation}(${metric.field})`
    }));
  };

  return (
    <Stack gap="md">
      <Group grow align="flex-start">
        <Select
          label={t( 'widget_builder_cartesian_xaxis_label','X-Axis (Label)' )}
          description={t( 'widget_builder_cartesian_xaxis_description','Select dimension for x-axis' )}
          placeholder={t( 'widget_builder_cartesian_xaxis_placeholder','Select dimension' )}
          data={getDimensionOptions()}
          value={widgetComponentProps.xAxis || ''}
          onChange={( value ) => handleUpdateProps( 'xAxis', value )}
          clearable={false}
        />
        <Select
          label={t( 'widget_builder_cartesian_yaxis_label','Y-Axis (Metric)' )}
          description={t( 'widget_builder_cartesian_yaxis_description','Select metric for y-axis' )}
          placeholder={t( 'widget_builder_cartesian_yaxis_placeholder','Select metric' )}
          data={getMetricOptions()}
          value={getMetricOptions().find( mo => mo.label === widgetComponentProps.yAxis )?.value || ''}
          onChange={( value, option ) => handleUpdateProps( 'yAxis', option.label )}
          clearable={false}
        />
      </Group>
      <Select
        label={t( 'widget_builder_cartesian_series_label','Series' )}
        description={t( 'widget_builder_cartesian_series_description','Select dimension for series (different from x-axis)' )}
        placeholder={t( 'widget_builder_cartesian_series_placeholder','Select dimension' )}
        data={getDimensionOptions().filter( dim => dim.value !== widgetComponentProps.xAxis )}
        value={widgetComponentProps.series || ''}
        onChange={( value ) => handleUpdateProps( 'series', value )}
        clearable={false}
      />
      <Divider label={t( 'widget_builder_props_display_options','Display Options' )} labelPosition="center" />
      <Group grow>
        <Switch
          label={t( 'widget_builder_cartesian_show_legend','Show Legend' )}
          checked={widgetComponentProps.legend !== false}
          onChange={( event ) => handleUpdateProps( 'legend', event.currentTarget.checked )}
        />
        <Switch
          label={t( 'widget_builder_cartesian_show_tooltips','Show Tooltips' )}
          checked={widgetComponentProps.tooltips !== false}
          onChange={( event ) => handleUpdateProps( 'tooltips', event.currentTarget.checked )}
        />
      </Group>
    </Stack>
  );
};

// Area Chart specific props component
const AreaChartPropsComponent = ({ config, updateConfig }: WidgetPropsComponentProps ) => {
  const widgetComponentProps = config.widgetComponentProps || {};
  const baseProps = CartesianChartPropsComponent({ config, updateConfig });
  
  const handleUpdateProps = ( key: string, value: any ) => {
    const updatedProps = {
      ...widgetComponentProps,
      [key]: value
    };
    updateConfig( 'widgetComponentProps', updatedProps );
  };

  return (
    <>
      {baseProps}
      <Divider label="Area Chart Options" labelPosition="center" />
      <Group grow>
        <Switch
          label="Use Gradient Fill"
          checked={widgetComponentProps.useGradient !== false}
          onChange={( event ) => handleUpdateProps( 'useGradient', event.currentTarget.checked )}
        />
      </Group>
    </>
  );
};

// Line Chart specific props component
const LineChartPropsComponent = ({ config, updateConfig }: WidgetPropsComponentProps ) => {
  const { t } = useTranslation( 'widgets' );
  const widgetComponentProps = config.widgetComponentProps || {};
  const baseProps = CartesianChartPropsComponent({ config, updateConfig });

  const handleUpdateProps = ( key: string, value: any ) => {
    updateConfig( 'widgetComponentProps', { ...widgetComponentProps, [key]: value });
  };

  return (
    <>
      {baseProps}
      <Divider label={t( 'widget_builder_line_chart_options_title','Line Chart Options' )} labelPosition="center" />
      <Group grow>
        <Switch
          label={t( 'widget_builder_line_show_data_points','Show Data Points' )}
          checked={widgetComponentProps.showDataPoints !== false}
          onChange={e => handleUpdateProps( 'showDataPoints', e.currentTarget.checked )}
        />
      </Group>
    </>
  );
};

// Bar Chart specific props component
const BarChartPropsComponent = ({ config, updateConfig }: WidgetPropsComponentProps ) => {
  const { t } = useTranslation( 'widgets' );
  const widgetComponentProps = config.widgetComponentProps || {};
  const dimensions = config.dimensions || [];
  const metrics = config.metrics || [];

  const handleUpdateProps = ( key: string, value: any ) =>
    updateConfig( 'widgetComponentProps', { ...widgetComponentProps, [key]: value });

  const getDimensionOptions = () =>
    dimensions.map( d => ({ value: d.field, label: d.label || d.field }));
  const getMetricOptions = () =>
    metrics.map( m => ({ value: m.field, label: m.label || `${m.operation}(${m.field})` }));

  return (
    <Stack gap="md">
      <Group grow align="flex-start">
        <Select
          label={t( 'widget_builder_bar_xaxis_label','X-Axis (Label)' )}
          description={t( 'widget_builder_bar_xaxis_description','Select dimension for x-axis' )}
          placeholder={t( 'widget_builder_bar_xaxis_placeholder','Select dimension' )}
          data={getDimensionOptions()}
          value={widgetComponentProps.xAxis || ''}
          onChange={v => handleUpdateProps( 'xAxis', v )}
          clearable={false}
        />
        <Select
          label={t( 'widget_builder_bar_yaxis_label','Y-Axis (Metric)' )}
          description={t( 'widget_builder_bar_yaxis_description','Select metric for y-axis' )}
          placeholder={t( 'widget_builder_bar_yaxis_placeholder','Select metric' )}
          data={getMetricOptions()}
          value={getMetricOptions().find( mo => mo.label === widgetComponentProps.yAxis )?.value || ''}
          onChange={( v,opt ) => handleUpdateProps( 'yAxis', opt.label )}
          clearable={false}
        />
      </Group>

      <Divider label={t( 'widget_builder_props_display_options','Display Options' )} labelPosition="center" />
      <Group grow>
        <Switch
          label={t( 'widget_builder_bar_show_legend','Show Legend' )}
          checked={widgetComponentProps.legend !== false}
          onChange={e => handleUpdateProps( 'legend', e.currentTarget.checked )}
        />
        <Switch
          label={t( 'widget_builder_bar_show_tooltips','Show Tooltips' )}
          checked={widgetComponentProps.tooltips !== false}
          onChange={e => handleUpdateProps( 'tooltips', e.currentTarget.checked )}
        />
      </Group>

      <Divider label={t( 'widget_builder_bar_options_title','Bar Chart Options' )} labelPosition="center" />
      <Group grow>
        <Select
          label={t( 'widget_builder_bar_orientation_label','Orientation' )}
          description={t( 'widget_builder_bar_orientation_description','Select bar orientation' )}
          placeholder={t( 'widget_builder_bar_orientation_placeholder','Select orientation' )}
          data={[
            { value:'vertical', label:t( 'widget_builder_bar_orientation_option_vertical','Vertical' ) },
            { value:'horizontal', label:t( 'widget_builder_bar_orientation_option_horizontal','Horizontal' ) }
          ]}
          value={widgetComponentProps.orientation || 'vertical'}
          onChange={v => handleUpdateProps( 'orientation', v )}
          clearable={false}
        />
      </Group>
    </Stack>
  );
};

// Shared component for radial charts (pie, donut)
const RadialChartPropsComponent = ({ config, updateConfig }: WidgetPropsComponentProps ) => {
  const { t } = useTranslation( 'widgets' );
  const widgetProps = config.widgetComponentProps || {};
  const dimensions = config.dimensions || [];
  const metrics = config.metrics || [];

  const handleUpdate = ( key: string, value: any ) =>
    updateConfig( 'widgetComponentProps', { ...widgetProps, [key]: value });

  const dimensionOptions = dimensions.map( d => ({ value: d.field, label: d.label || d.field }));
  const metricOptions = metrics.map( m => ({ value: m.field, label: m.label || `${m.operation}(${m.field})` }));

  return (
    <Stack gap="md">
      <Select
        label={t( 'widget_builder_radial_group_by_label','Group By (Dimension)' )}
        description={t( 'widget_builder_radial_group_by_description','Select dimension to group slices' )}
        data={dimensionOptions}
        value={widgetProps.groupBy || ''}
        onChange={v => handleUpdate( 'groupBy', v )}
        clearable={false}
      />
      <Select
        label={t( 'widget_builder_radial_value_metric_label','Value Metric' )}
        description={t( 'widget_builder_radial_value_metric_description','Select metric for slice size' )}
        data={metricOptions}
        value={metricOptions.find( mo => mo.label === widgetProps.valueMetric )?.value || ''}
        onChange={( v,opt ) => handleUpdate( 'valueMetric', opt.label )}
        clearable={false}
      />
      <Group grow>
        <Switch
          label={t( 'widget_builder_radial_show_labels','Show Labels' )}
          checked={widgetProps.withLabels !== false}
          onChange={e => handleUpdate( 'withLabels', e.currentTarget.checked )}
        />
      </Group>
    </Stack>
  );
};

// Pie Chart specific props component
const PieChartPropsComponent = RadialChartPropsComponent;

// Donut Chart specific props component
const DonutChartPropsComponent = RadialChartPropsComponent;

// Map of widget types to their props components
export const widgetPropsComponents: Partial<Record<WidgetType, React.ComponentType<WidgetPropsComponentProps>>> = {
  areaChart: AreaChartPropsComponent,
  barChart: BarChartPropsComponent,
  lineChart: LineChartPropsComponent,
  pieChart: PieChartPropsComponent,
  donutChart: DonutChartPropsComponent,
  // TV1-1930 (S15) — the SAME component the web builder uses. The KPI's props are
  // device-independent, and a second copy would only be a second place for the
  // `goodDirection` default (no colour) to drift.
  kpi: KpiPropsComponent
};

// Function to get the appropriate props component based on widget type
export const getMobileWidgetPropsComponent = ( widgetType: WidgetType ): React.ComponentType<WidgetPropsComponentProps> => {
  const component = widgetPropsComponents[widgetType];
  return component || DefaultWidgetPropsComponent;
};